/**
 * Hook to protect OAuth flows from interference
 * Prevents premature cleanup and provides state validation
 */

import { useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { logger } from '@/services/LoggingService';

interface UseOAuthProtectionOptions {
  provider?: 'wealthica2' | 'vezgo';
  onFlowDetected?: (flowType: string) => void;
  onFlowCompleted?: () => void;
}

export function useOAuthProtection(options: UseOAuthProtectionOptions = {}) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const flowStateRef = useRef<{
    isActive: boolean;
    startTime: number | null;
    flowType: string | null;
  }>({
    isActive: false,
    startTime: null,
    flowType: null
  });

  /**
   * Detect if OAuth flow is currently active
   */
  const detectOAuthFlow = useCallback((): boolean => {
    try {
      // Check for Wealthica callback parameters
      const hasWealthicaCallback = searchParams.has('institution') || 
                                 searchParams.has('code');
      
      // Check for Vezgo callback parameters
      const hasVezgoCallback = searchParams.has('account');
      
      // Check for OAuth state parameter
      const hasOAuthState = searchParams.has('state');
      
      // Check if we're on an auth-related page
      const isOnAuthPage = location.pathname.includes('/auth') ||
                          location.pathname.includes('/callback') ||
                          location.pathname.includes('/connect');
      
      const isOAuthFlow = (hasWealthicaCallback || hasVezgoCallback) && 
                         hasOAuthState && 
                         isOnAuthPage;
      
      console.log('[useOAuthProtection] detectOAuthFlow check', {
        isOAuthFlow,
        hasWealthicaCallback,
        hasVezgoCallback,
        hasOAuthState,
        isOnAuthPage,
        pathname: location.pathname,
        search: location.search,
        currentFlowState: flowStateRef.current,
        url: window.location.href
      });
      
      if (isOAuthFlow && !flowStateRef.current.isActive) {
        const flowType = hasWealthicaCallback ? 'wealthica2' : 'vezgo';
        
        flowStateRef.current = {
          isActive: true,
          startTime: Date.now(),
          flowType
        };
        
        console.log('[useOAuthProtection] OAuth flow DETECTED and ACTIVATED', {
          flowType,
          provider: options.provider,
          url: window.location.href,
          timestamp: new Date().toISOString()
        });
        
        logger.info('useOAuthProtection', 'OAuth flow detected', {
          flowType,
          provider: options.provider,
          url: window.location.href,
          timestamp: new Date().toISOString()
        });
        
        options.onFlowDetected?.(flowType);
      }
      // CRITICAL FIX: Don't mark flow as completed just because URL params change
      // Keep protection active during callback processing until explicitly completed
      
      const result = isOAuthFlow || flowStateRef.current.isActive;
      console.log('[useOAuthProtection] detectOAuthFlow result', {
        result,
        isOAuthFlow,
        flowActive: flowStateRef.current.isActive,
        flowState: flowStateRef.current
      });
      
      return result;
    } catch (error) {
      logger.error('useOAuthProtection', 'Error detecting OAuth flow', {
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }, [searchParams, location.pathname, options]);

  /**
   * Check if Wealthica connection data is valid and should be preserved
   * Checks both sessionStorage (primary) and localStorage (fallback)
   */
  const hasValidWealthicaConnection = useCallback((): boolean => {
    try {
      // Check sessionStorage first (primary storage)
      let connectionData = sessionStorage.getItem('wealthica2_connection_data');
      let token = sessionStorage.getItem('wealthica2_bearer_token');
      
      // Fallback to localStorage if not in sessionStorage
      if (!connectionData || !token) {
        connectionData = connectionData || localStorage.getItem('wealthica2_connection_data');
        token = token || localStorage.getItem('wealthica2_bearer_token');
      }
      
      if (connectionData && token) {
        const parsed = JSON.parse(connectionData);
        const hasValidData = parsed?.institutionId && parsed?.providerName;
        return hasValidData && (token?.length || 0) > 0;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  /**
   * Check for recent auth state changes (30 second protection window)
   */
  const hasRecentAuthStateChange = useCallback((): boolean => {
    try {
      const lastChange = sessionStorage.getItem('auth_state_change_timestamp');
      if (!lastChange) return false;
      const timeDiff = Date.now() - parseInt(lastChange);
      return timeDiff < 30000; // 30 second protection window
    } catch {
      return false;
    }
  }, []);

  /**
   * Check if storage cleanup should be blocked
   * Two-tier protection: OAuth flows OR valid connections
   */
  const shouldBlockCleanup = useCallback((): boolean => {
    // Tier 1: Active OAuth flow protection
    const isOAuthActive = detectOAuthFlow();
    
    // Tier 2: Recent OAuth activity protection (5 minute window)
    const hasRecentOAuthActivity = flowStateRef.current.startTime && 
                                  (Date.now() - flowStateRef.current.startTime) < 300000;
    
    // Tier 3: Valid connection data protection (CRITICAL for manual refresh)
    const hasValidConnection = hasValidWealthicaConnection();
    
    // Tier 4: Recent auth state changes protection (30 second window)
    const hasRecentAuth = hasRecentAuthStateChange();
    
    // Block cleanup if ANY protection tier is active
    const shouldBlock = isOAuthActive || !!hasRecentOAuthActivity || hasValidConnection || hasRecentAuth;
    
    console.log('[useOAuthProtection] shouldBlockCleanup check', {
      shouldBlock,
      isOAuthActive,
      hasRecentOAuthActivity: !!hasRecentOAuthActivity,
      hasValidConnection,
      hasRecentAuth,
      flowState: flowStateRef.current,
      pathname: location.pathname,
      url: window.location.href,
      protectionTiers: {
        tier1_oauth: isOAuthActive,
        tier2_recent: !!hasRecentOAuthActivity,
        tier3_connection: hasValidConnection,
        tier4_auth: hasRecentAuth
      }
    });
    
    return shouldBlock;
  }, [detectOAuthFlow, hasValidWealthicaConnection, hasRecentAuthStateChange, location.pathname]);

  /**
   * Force complete the OAuth flow (for error recovery)
   */
  const forceCompleteFlow = useCallback(() => {
    logger.info('useOAuthProtection', 'Forcing OAuth flow completion', {
      previousState: flowStateRef.current
    });
    
    flowStateRef.current = {
      isActive: false,
      startTime: null,
      flowType: null
    };
    
    options.onFlowCompleted?.();
  }, [options]);

  // Monitor OAuth flow state changes
  useEffect(() => {
    detectOAuthFlow();
  }, [detectOAuthFlow]);

  // Auto-cleanup after timeout (5 minutes)
  useEffect(() => {
    if (flowStateRef.current.isActive && flowStateRef.current.startTime) {
      const timeoutId = setTimeout(() => {
        if (flowStateRef.current.isActive) {
          logger.warn('useOAuthProtection', 'Auto-completing OAuth flow due to timeout');
          forceCompleteFlow();
        }
      }, 300000); // 5 minutes

      return () => clearTimeout(timeoutId);
    }
  }, [flowStateRef.current.isActive, forceCompleteFlow]);

  return {
    isOAuthFlowActive: flowStateRef.current.isActive,
    flowType: flowStateRef.current.flowType,
    shouldBlockCleanup,
    forceCompleteFlow,
    detectOAuthFlow
  };
}